import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faWarehouse } from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import { useTranslation } from 'react-i18next';
import SearchableSelect from '../common/SearchableSelect';
import locationsService from '../../services/locationsService';
import customerWarehousesService from '../../services/customerWarehousesService';
import { useMessageAlert } from '../common/MessageAlert';

const INITIAL_FORM = {
  name: '',
  locationId: '',
  country: 'India',
  state: '',
  city: '',
  address: '',
  mapUrl: '',
  contactPerson: '',
  contactPhone: '',
  isDefault: false,
};

/**
 * CustomerWarehouseForm
 *
 * Modal that auto-opens after a customer is created to assign a warehouse.
 * Supports two modes toggled by a checkbox:
 * - Existing location: pick from dropdown, location fields auto-filled & read-only
 * - New location: dropdown hidden, all fields manually editable
 *
 * Props:
 * - customerId: the newly created customer's id
 * - isOpen: controls visibility
 * - onClose: called on skip or after successful save
 * - initialData: warehouse object for editing
 */
const CustomerWarehouseForm = ({ customerId, isOpen, onClose, isOffline = false, onSave, initialData = null }) => {
  const { t } = useTranslation();
  const [formValues, setFormValues] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // true = pick an existing location; false = enter new location manually
  const [useExisting, setUseExisting] = useState(false);

  const [rawLocations, setRawLocations] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [locationSearch, setLocationSearch] = useState('');

  const { showCreate } = useMessageAlert();

  const fetchLocations = useCallback(async (search = '') => {
    setLocationsLoading(true);
    try {
      const result = await locationsService.getLocations({
        skip: 0,
        limit: 100,
        ...(search ? { search } : {}),
      });
      if (result.success) {
        const data = Array.isArray(result.data) ? result.data : [];
        setRawLocations(data);
        setLocationOptions(
          data.map((loc) => ({
            value: loc.id,
            label: loc.delivery_location || loc.name || String(loc.id),
          }))
        );
      }
    } finally {
      setLocationsLoading(false);
    }
  }, []);

  // Reset on open
  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        setFormValues({
          name: initialData.name || '',
          locationId: initialData.location_id || '',
          country: initialData.country || 'India',
          state: initialData.state || '',
          city: initialData.city || '',
          address: initialData.address || '',
          mapUrl: initialData.map_url || '',
          contactPerson: initialData.contact_person || '',
          contactPhone: initialData.contact_phone || '',
          isDefault: initialData.is_default || false,
        });
        setUseExisting(!!initialData.location_id);
      } else {
        setFormValues(INITIAL_FORM);
        setUseExisting(true);
      }
      setErrors({});
      setLocationSearch('');
      fetchLocations();
    }
  }, [isOpen, fetchLocations, initialData]);

  // Debounced server-side search
  useEffect(() => {
    if (!isOpen) return;
    const timer = setTimeout(() => fetchLocations(locationSearch), 300);
    return () => clearTimeout(timer);
  }, [locationSearch, isOpen, fetchLocations]);

  // When a location is selected in existing mode, auto-populate location fields
  const handleLocationChange = (e) => {
    const selectedId = e.target.value;
    const loc = rawLocations.find((l) => String(l.id) === String(selectedId));
    setFormValues((prev) => ({
      ...prev,
      locationId: selectedId,
      country: loc?.country || 'India',
      state: loc?.state || '',
      city: loc?.city || '',
      address: loc?.delivery_location || '',
      mapUrl: loc?.map_url || '',
    }));
    if (errors.locationId) setErrors((prev) => ({ ...prev, locationId: null }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const handleToggleMode = (e) => {
    const isExisting = e.target.checked;
    setUseExisting(isExisting);
    // Clear location-derived fields when switching modes
    setFormValues((prev) => ({
      ...prev,
      locationId: '',
      country: 'India',
      state: '',
      city: '',
      address: '',
      mapUrl: '',
    }));
    setErrors({});
  };

  const validate = () => {
    const next = {};
    if (!formValues.name.trim()) next.name = t('common.formFields.errors.warehouseNameRequired');
    if (useExisting && !formValues.locationId) next.locationId = t('common.formFields.errors.selectLocation');

    // When a location is being created rather than picked, state / city / address are
    // all required — they map to NOT NULL columns and the API rejects a blank one with
    // a 422. Without this the form submitted happily and the failure only surfaced as a
    // server error, and `city` in particular is what prints as `Location:` on challans.
    if (!useExisting) {
      if (!String(formValues.state || '').trim()) next.state = t('common.formFields.errors.stateRequired');
      if (!String(formValues.city || '').trim()) next.city = t('common.formFields.errors.cityRequired');
      if (!String(formValues.address || '').trim()) next.address = t('common.formFields.errors.addressRequired');
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const buildPayload = () => ({
    name: formValues.name,
    location_id: useExisting && formValues.locationId ? Number(formValues.locationId) : null,
    country: formValues.country || 'India',
    state: formValues.state,
    city: formValues.city,
    address: formValues.address,
    map_url: formValues.mapUrl,
    contact_person: formValues.contactPerson,
    contact_phone: formValues.contactPhone,
    is_default: formValues.isDefault,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    if (isOffline) {
      if (onSave) onSave(buildPayload());
      showCreate(t('customerWarehouses.messages.addedToList', { name: formValues.name }));
      onClose();
      return;
    }

    setSubmitting(true);
    try {
      const payload = buildPayload();
      const result = initialData?.id 
        ? await customerWarehousesService.updateWarehouse(customerId, initialData.id, payload)
        : await customerWarehousesService.createWarehouse(customerId, payload);
        
      if (result.success) {
        showCreate(t(initialData?.id ? 'customerWarehouses.messages.updated' : 'customerWarehouses.messages.assigned', { name: formValues.name }));
        onClose();
      } else {
        setErrors({ submit: result.error || (initialData?.id ? t('customerWarehouses.errors.updateFailed') : t('customerWarehouses.errors.createFailed')) });
      }
    } catch {
      setErrors({ submit: initialData?.id ? t('customerWarehouses.errors.updateFailed') : t('customerWarehouses.errors.createFailed') });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveAndAddAnother = async () => {
    if (!validate()) return;

    if (isOffline) {
      if (onSave) onSave(buildPayload());
      showCreate(t('customerWarehouses.messages.addedToList', { name: formValues.name }));
      setFormValues(INITIAL_FORM);
      setErrors({});
      setUseExisting(true);
      setLocationSearch('');
      return;
    }

    setSubmitting(true);
    try {
      const result = await customerWarehousesService.createWarehouse(customerId, buildPayload());
      if (result.success) {
        showCreate(t('customerWarehouses.messages.assigned', { name: formValues.name }));
        setFormValues(INITIAL_FORM);
        setErrors({});
        setUseExisting(false);
        setLocationSearch('');
      } else {
        setErrors({ submit: result.error || t('customerWarehouses.errors.createFailed') });
      }
    } catch {
      setErrors({ submit: t('customerWarehouses.errors.createFailed') });
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const inputClass = 'w-full rounded-xl border px-4 py-3 text-sm outline-none';

  const inputStyle = (field, readOnly = false) => ({
    backgroundColor: readOnly ? UI_COLORS.FORMS.INPUT.disabled || '#f3f4f6' : UI_COLORS.FORMS.INPUT.background,
    borderColor: errors[field] ? UI_COLORS.BORDERS.ERROR : UI_COLORS.FORMS.INPUT.border,
    color: readOnly ? UI_COLORS.TEXT.MUTED : UI_COLORS.FORMS.INPUT.text,
    cursor: readOnly ? 'not-allowed' : 'text',
  });

  // In existing mode, location-derived fields are always disabled (populated on selection)
  const locationFields = useExisting;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{ backgroundColor: UI_COLORS.BACKGROUNDS.OVERLAY }}
    >
      <div className="w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl">
        {/* Header */}
        <div
          className="flex items-center justify-between border-b px-6 py-5"
          style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}
        >
          <div className="flex items-center gap-3">
            <div
              className="flex h-10 w-10 items-center justify-center rounded-full"
              style={{
                backgroundColor: colorHelpers.withOpacity(UI_COLORS.BUTTONS.PRIMARY.background, 0.12),
                color: UI_COLORS.BUTTONS.PRIMARY.background,
              }}
            >
              <FontAwesomeIcon icon={faWarehouse} />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: UI_COLORS.TEXT.MUTED }}>
                {initialData ? t('customerWarehouses.headings.updateDetails') : t('customerWarehouses.headings.optionalStep')}
              </p>
              <h2 className="text-xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                {initialData ? t('customerWarehouses.headings.editWarehouse') : t('customerWarehouses.headings.assign')}
              </h2>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 transition-all duration-200 hover:scale-105"
            style={{
              color: UI_COLORS.TEXT.MUTED,
              backgroundColor: colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.1),
            }}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="max-h-[75vh] overflow-y-auto px-6 py-6">
          {/* Mode toggle */}
          <div
            className="mb-5 flex items-center gap-3 rounded-xl border px-4 py-3"
            style={{ borderColor: UI_COLORS.BORDERS.LIGHT, backgroundColor: colorHelpers.withOpacity(UI_COLORS.BUTTONS.PRIMARY.background, 0.04) }}
          >
            <input
              id="useExistingToggle"
              type="checkbox"
              checked={useExisting}
              onChange={handleToggleMode}
              className="h-4 w-4 rounded"
              style={{ accentColor: UI_COLORS.BUTTONS.PRIMARY.background }}
            />
            <label htmlFor="useExistingToggle" className="cursor-pointer select-none text-sm font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
              {t('customerWarehouses.labels.useExistingLocation')}
            </label>
            <span className="text-xs" style={{ color: UI_COLORS.TEXT.MUTED }}>
              {useExisting
                ? t('customerWarehouses.messages.selectToAutofill')
                : t('customerWarehouses.messages.enterManually')}
            </span>
          </div>

          <div className="grid gap-5 md:grid-cols-2">
            {/* Warehouse Name */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                {t('common.formFields.labels.warehouseName')} <span style={{ color: UI_COLORS.STATUS.ERROR }}>*</span>
              </label>
              <input
                name="name"
                value={formValues.name}
                onChange={handleChange}
                placeholder={t('common.formFields.placeholders.warehouseName')}
                className={inputClass}
                style={inputStyle('name')}
              />
              {errors.name && <p className="text-xs" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.name}</p>}
            </div>

            {/* Location dropdown — only in existing mode */}
            {useExisting && (
              <div className="flex flex-col gap-2">
                <label className="text-sm font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                  {t('common.formFields.labels.deliveryLocation')} <span style={{ color: UI_COLORS.STATUS.ERROR }}>*</span>
                </label>
                <SearchableSelect
                  name="locationId"
                  value={formValues.locationId}
                  options={locationOptions}
                  onChange={handleLocationChange}
                  placeholder={t('common.formFields.placeholders.deliveryLocationSearch')}
                  loading={locationsLoading}
                  onSearchChange={setLocationSearch}
                  error={errors.locationId}
                />
                {errors.locationId && (
                  <p className="text-xs" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.locationId}</p>
                )}
              </div>
            )}

            {/* Country */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                {t('common.formFields.labels.country')}
                {locationFields && <span className="ml-2 text-xs font-normal" style={{ color: UI_COLORS.TEXT.MUTED }}>{t('customerWarehouses.messages.fromLocation')}</span>}
              </label>
              <input
                name="country"
                value={formValues.country}
                onChange={handleChange}
                placeholder={t('common.formFields.placeholders.india')}
                readOnly={!!locationFields}
                className={inputClass}
                style={inputStyle('country', !!locationFields)}
              />
            </div>

            {/* State */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                {t('common.formFields.labels.state')}
                {!locationFields && <span className="ml-1" style={{ color: UI_COLORS.STATUS.ERROR }}>*</span>}
                {locationFields && <span className="ml-2 text-xs font-normal" style={{ color: UI_COLORS.TEXT.MUTED }}>{t('customerWarehouses.messages.fromLocation')}</span>}
              </label>
              <SearchableSelect
                name="state"
                value={formValues.state}
                options={t('locations.states', { returnObjects: true })}
                onChange={handleChange}
                placeholder={t('common.formFields.placeholders.selectState')}
                disabled={!!locationFields}
                error={errors.state}
              />
            </div>

            {/* City */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                {t('common.formFields.labels.city')}
                {!locationFields && <span className="ml-1" style={{ color: UI_COLORS.STATUS.ERROR }}>*</span>}
                {locationFields && <span className="ml-2 text-xs font-normal" style={{ color: UI_COLORS.TEXT.MUTED }}>{t('customerWarehouses.messages.fromLocation')}</span>}
              </label>
              <input
                name="city"
                value={formValues.city}
                onChange={handleChange}
                placeholder={t('common.formFields.placeholders.enterCity')}
                readOnly={!!locationFields}
                className={inputClass}
                style={inputStyle('city', !!locationFields)}
              />
              {errors.city && <p className="text-xs" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.city}</p>}
            </div>

            {/* Contact Person */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{t('common.formFields.labels.contactPerson')}</label>
              <input
                name="contactPerson"
                value={formValues.contactPerson}
                onChange={handleChange}
                placeholder={t('common.formFields.placeholders.contactPerson')}
                className={inputClass}
                style={inputStyle('contactPerson')}
              />
            </div>

            {/* Contact Phone */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{t('common.formFields.labels.contactPhone')}</label>
              <input
                name="contactPhone"
                value={formValues.contactPhone}
                onChange={handleChange}
                placeholder={t('common.formFields.placeholders.contactPhone')}
                className={inputClass}
                style={inputStyle('contactPhone')}
              />
            </div>

            {/* Set as Default */}
            <div className="flex items-center gap-2 mt-4">
              <input
                type="checkbox"
                name="isDefault"
                id="isDefault"
                checked={formValues.isDefault}
                onChange={handleChange}
                className="w-4 h-4 rounded border-gray-300"
                style={{ accentColor: UI_COLORS.BRAND.PRIMARY }}
              />
              <label htmlFor="isDefault" className="text-sm font-semibold select-none cursor-pointer" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                {t('customerWarehouses.labels.setAsDefault')}
              </label>
            </div>

            {/* Map URL */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                {t('common.formFields.labels.mapUrl')}
                {locationFields && <span className="ml-2 text-xs font-normal" style={{ color: UI_COLORS.TEXT.MUTED }}>{t('customerWarehouses.messages.fromLocation')}</span>}
              </label>
              <input
                name="mapUrl"
                value={formValues.mapUrl}
                onChange={handleChange}
                placeholder={t('common.formFields.placeholders.mapUrl')}
                readOnly={!!locationFields}
                className={inputClass}
                style={inputStyle('mapUrl', !!locationFields)}
              />
            </div>

            {/* Address — full width */}
            <div className="flex flex-col gap-2 md:col-span-2">
              <label className="text-sm font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                {t('common.formFields.labels.address')}
                {!locationFields && <span className="ml-1" style={{ color: UI_COLORS.STATUS.ERROR }}>*</span>}
                {locationFields && <span className="ml-2 text-xs font-normal" style={{ color: UI_COLORS.TEXT.MUTED }}>{t('customerWarehouses.messages.fromLocation')}</span>}
              </label>
              <textarea
                name="address"
                value={formValues.address}
                onChange={handleChange}
                placeholder={t('common.formFields.placeholders.warehouseAddress')}
                rows={3}
                readOnly={!!locationFields}
                className={inputClass}
                style={inputStyle('address', !!locationFields)}
              />
              {errors.address && <p className="text-xs" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.address}</p>}
            </div>
          </div>

          {errors.submit && (
            <p className="mt-3 text-sm" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.submit}</p>
          )}

          <div
            className="mt-6 flex items-center justify-end gap-3 border-t pt-5"
            style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}
          >
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="rounded-xl px-5 py-2.5 text-sm font-semibold transition-all duration-200 hover:scale-105 disabled:opacity-60"
              style={{
                backgroundColor: 'transparent',
                color: UI_COLORS.TEXT.MUTED,
                border: `1px solid ${UI_COLORS.BORDERS.MEDIUM}`,
              }}
            >
              {t('common.buttons.done')}
            </button>
            {!initialData && (
              <button
                type="button"
                onClick={handleSaveAndAddAnother}
                disabled={submitting}
                className="rounded-xl px-5 py-2.5 text-sm font-semibold transition-all duration-200 hover:scale-105 disabled:cursor-not-allowed disabled:opacity-70"
                style={{
                  backgroundColor: 'transparent',
                  color: UI_COLORS.BUTTONS.PRIMARY.background,
                  border: `1px solid ${UI_COLORS.BUTTONS.PRIMARY.background}`,
                }}
              >
                {submitting ? t('common.buttons.saving') : t('customerWarehouses.buttons.saveAndAddAnother')}
              </button>
            )}
            <button
              type="submit"
              disabled={submitting}
              className="rounded-xl px-5 py-2.5 text-sm font-semibold transition-all duration-200 hover:scale-105 disabled:cursor-not-allowed disabled:opacity-70"
              style={{
                backgroundColor: UI_COLORS.BUTTONS.PRIMARY.background,
                color: UI_COLORS.BUTTONS.PRIMARY.text,
              }}
            >
              {submitting ? t('common.buttons.saving') : (initialData ? t('customerWarehouses.buttons.updateWarehouse') : t('customerWarehouses.buttons.saveWarehouse'))}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CustomerWarehouseForm;
